import { ArrowLeftIcon, CheckCircleIcon, ClockIcon, CreditCardIcon, DocumentTextIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import React, { useEffect, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { PaymentStatus } from '../../@types/PaymentStatus';
import type { OfferDetails } from '../../services/paymentApi';
import { paymentApi } from '../../services/paymentApi';
import type { RootState } from '../../store/store';
import { ROUTES } from '../../utils/routes';
import { hasActiveSubscription } from '../../utils/subscriptions';
import { showToast } from '../common/Notification';

/**
 * Check if the company has a "no fee" payment status (onboarding fees are waived)
 */
const hasNoOnboardingFee = (paymentStatus: PaymentStatus | string | null | undefined): boolean => {
  return paymentStatus === PaymentStatus.INACTIVE_UNDER_1000_NO_FEE 
    || paymentStatus === PaymentStatus.INACTIVE_1000_TO_2000_NO_FEE
    || paymentStatus === PaymentStatus.INACTIVE_OVER_2000_NO_FEE;
};

/**
 * Check if the company has a 50% promotional discount on onboarding fees
 * Also returns true for default inactive status or null/undefined (fallback)
 */
const has50PercentPromo = (paymentStatus: PaymentStatus | string | null | undefined): boolean => {
  // Explicit 50% promo statuses
  if (paymentStatus === PaymentStatus.INACTIVE_UNDER_1000_50_PROMO 
    || paymentStatus === PaymentStatus.INACTIVE_1000_TO_2000_50_PROMO
    || paymentStatus === PaymentStatus.INACTIVE_OVER_2000_50_PROMO) {
    return true;
  }
  // Default/fallback: inactive or null/undefined status gets 50% discount
  if (paymentStatus === PaymentStatus.INACTIVE || !paymentStatus) {
    return true;
  }
  return false;
};

/**
 * Check if the company has an "under 1000" payment status
 */
const isUnder1000Status = (paymentStatus: PaymentStatus | string | null | undefined): boolean => {
  return paymentStatus === PaymentStatus.INACTIVE_UNDER_1000 
    || paymentStatus === PaymentStatus.INACTIVE_UNDER_1000_NO_FEE
    || paymentStatus === PaymentStatus.INACTIVE_UNDER_1000_50_PROMO
    || paymentStatus === PaymentStatus.ACTIVE_UNDER_1000;
};

/**
 * Check if the company has a "1000 to 2000" payment status
 */
const is1000To2000Status = (paymentStatus: PaymentStatus | string | null | undefined): boolean => {
  return paymentStatus === PaymentStatus.INACTIVE_1000_TO_2000 
    || paymentStatus === PaymentStatus.INACTIVE_1000_TO_2000_NO_FEE
    || paymentStatus === PaymentStatus.INACTIVE_1000_TO_2000_50_PROMO
    || paymentStatus === PaymentStatus.ACTIVE_1000_TO_2000;
};

/**
 * Get the onboarding fee amount based on payment status
 * Pricing tiers:
 * - 0-1000 lots: 490€ starter fees
 * - 1000-2000 lots: 980€ starter fees
 * - 2000+ lots: 1980€ starter fees
 */
const getOnboardingFeeAmount = (paymentStatus: PaymentStatus | string | null | undefined): number => {
  if (isUnder1000Status(paymentStatus)) {
    return 490;
  }
  if (is1000To2000Status(paymentStatus)) {
    return 980;
  }
  return 1980; // 2000+ lots
};

/**
 * Get the discounted onboarding fee amount (50% off)
 */
const getDiscountedOnboardingFeeAmount = (paymentStatus: PaymentStatus | string | null | undefined): number => {
  const baseAmount = getOnboardingFeeAmount(paymentStatus);
  return Math.round(baseAmount * 0.5); // 50% off
};

const CGV_URL = process.env.REACT_APP_CGV_URL || '/documents/CGV.pdf';

const PaymentCheckout: React.FC = () => {
  const navigate = useNavigate();
  const { company, token } = useSelector((state: RootState) => state.auth);
  
  const [loading, setLoading] = useState(true);
  const [offerDetails, setOfferDetails] = useState<OfferDetails | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isCreatingSubscription, setIsCreatingSubscription] = useState(false);
  const [acceptedCGV, setAcceptedCGV] = useState(false);
  const [paymentLink, setPaymentLink] = useState<string | null>(null);

  // Redirect if already subscribed
  useEffect(() => {
    if (company && hasActiveSubscription(company)) {
      showToast('info', 'Vous avez déjà un abonnement actif');
      navigate(ROUTES.HOME);
    }
  }, [company, navigate]);

  // Load offer details - wait for both company and token to be available
  useEffect(() => {
    const loadOfferDetails = async() => {
      // Don't attempt to load until both company and token are ready
      if (!company?.id || !token) {
        setLoading(true);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const offer = await paymentApi.getOffer(company.id, token);
        setOfferDetails(offer);
      } catch (err) {
        console.error('Error loading offer:', err);
        setError('Impossible de charger les détails de l\'offre. Veuillez réessayer.');
      } finally {
        setLoading(false);
      }
    };

    loadOfferDetails();
  }, [company?.id, token]);

  const formatPrice = (cents: number) => {
    return `${(cents / 100).toFixed(2)} €`;
  };

  const handleProceedToPayment = async() => {
    if (!acceptedCGV) {
      showToast('error', 'Vous devez accepter les Conditions Générales de Vente pour continuer');
      return;
    }

    if (!company || !token) {
      showToast('error', 'Aucune entreprise sélectionnée');
      return;
    }

    try {
      setIsCreatingSubscription(true);
      const response = await paymentApi.createSubscription(company.id, token);
      
      if (response.paymentLink) {
        setPaymentLink(response.paymentLink);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else if (response.subscription && (response.subscription.Status === 'Active' || response.subscription.StateSubscription === 'ActiveRunning')) {
        showToast('success', 'Nous vérifions votre abonnement et le lancerons dans les plus brefs délais.');
        navigate(ROUTES.HOME);
      } else {
        console.error('Subscription creation failed:', response);
        showToast('error', 'Impossible de créer l\'abonnement. Veuillez réessayer.');
      }
    } catch (err) {
      console.error('Error creating subscription:', err);
      showToast('error', 'Une erreur est survenue lors de la création de l\'abonnement');
    } finally {
      setIsCreatingSubscription(false);
    }
  };

  const handleGoBack = () => {
    navigate(ROUTES.HOME);
  };

  if (paymentLink) {
    return (
      <div className="flex min-h-screen flex-col bg-white">
        <div className="z-10 flex items-center justify-between border-b border-gray-200 bg-white px-4 py-3 shadow-sm">
          <button
            onClick={() => setPaymentLink(null)}
            className="flex items-center text-gray-600 transition-colors hover:text-gray-900"
          >
            <ArrowLeftIcon className="mr-2 h-5 w-5" />
            <span className="font-medium">Retour au récapitulatif</span>
          </button>
          <div className="text-sm text-gray-500">
            Paiement sécurisé par ProAbono
          </div>
        </div>
        <iframe
          src={paymentLink}
          className="h-full w-full flex-1 border-0"
          title="Paiement ProAbono"
          allow="payment"
          style={{ minHeight: 'calc(100vh - 60px)' }}
        />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 px-4 py-12 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-4xl">
          <Skeleton height={60} className="mb-6" />
          <Skeleton height={400} />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 px-4 py-12 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-4xl">
          <div className="rounded-lg bg-white p-8 shadow-lg">
            <div className="mb-6 flex items-center gap-4">
              <ExclamationTriangleIcon className="h-12 w-12 text-red-500" />
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Erreur</h2>
                <p className="text-gray-600">{error}</p>
              </div>
            </div>
            <button
              onClick={handleGoBack}
              className="rounded-lg bg-gray-600 px-6 py-2 font-semibold text-white transition-colors hover:bg-gray-700"
            >
              Retour
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 px-4 py-12 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-4xl">
        {/* Header */}
        <div className="mb-8 text-center">
          <h1 className="mb-2 text-4xl font-bold text-gray-900">
            Finaliser votre abonnement
          </h1>
          <p className="text-lg text-gray-600">
            Accédez à tous les blocs fonctionnels du module Croissance de Déclarations Juridiques en quelques clics
          </p>
        </div>

        <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
          {/* Left Column - Offer Details */}
          <div className="space-y-6 lg:col-span-2">
            {/* Company Information */}
            {company && (
              <div className="rounded-lg bg-white p-6 shadow-md">
                <h2 className="mb-4 text-xl font-semibold text-gray-900">
                  Informations de facturation
                </h2>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Entreprise :</span>
                    <span className="font-semibold text-gray-900">{company.name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">SIREN :</span>
                    <span className="font-semibold text-gray-900">{company.siren}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Offer Details */}
            {offerDetails && (
              <div className="rounded-lg bg-white p-6 shadow-md">
                <div className="mb-6 flex items-center gap-2">
                  <CreditCardIcon className="h-6 w-6 text-blue-500" />
                  <h2 className="text-xl font-semibold text-gray-900">Détails de l'offre</h2>
                </div>

                <div className="mb-6 rounded-lg bg-gradient-to-r from-blue-600 to-blue-700 p-8 text-white">
                  <div className="text-center">
                    <div className="mb-2 text-5xl font-bold">
                      {formatPrice(offerDetails.AmountRecurrence)}
                    </div>
                    <div className="text-xl text-blue-100">HT par mois</div>
                    <div className="mt-2 text-sm text-blue-200">
                      Facturation mensuelle • Engagement 12 mois
                    </div>
                  </div>
                </div>

                {/* Onboarding Fee Banner - Different display based on fee status */}
                {hasNoOnboardingFee(company?.paymentStatus) ? (
                  <div className="mb-6 rounded-lg border-2 border-green-400 bg-gradient-to-r from-green-50 to-emerald-50 p-4">
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0">
                        <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
                        </svg>
                      </div>
                      <div className="flex-1">
                        <h4 className="mb-1 font-bold text-green-900"><span role="img" aria-label="party popper">🎉</span> Offre spéciale</h4>
                        <p className="text-sm text-green-800">
                          Frais de mise en service de <span className="font-bold line-through">{getOnboardingFeeAmount(company?.paymentStatus)}€ HT</span> <span className="font-bold text-green-600">OFFERTS</span> !
                        </p>
                      </div>
                    </div>
                  </div>
                ) : has50PercentPromo(company?.paymentStatus) ? (
                  <div className="mb-6 rounded-lg border-2 border-purple-400 bg-gradient-to-r from-purple-50 to-violet-50 p-4">
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0">
                        <svg className="h-6 w-6 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
                        </svg>
                      </div>
                      <div className="flex-1">
                        <h4 className="mb-1 font-bold text-purple-900"><span role="img" aria-label="sparkles">✨</span> Offre promotionnelle -50%</h4>
                        <p className="text-sm text-purple-800">
                          Frais de mise en service : <span className="font-bold text-gray-500 line-through">{getOnboardingFeeAmount(company?.paymentStatus)}€ HT</span> <span className="font-bold text-purple-600">{getDiscountedOnboardingFeeAmount(company?.paymentStatus)}€ HT</span> (-50%)
                        </p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="mb-6 rounded-lg border-2 border-amber-400 bg-gradient-to-r from-amber-50 to-orange-50 p-4">
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0">
                        <svg className="h-6 w-6 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <div className="flex-1">
                        <h4 className="mb-1 font-bold text-amber-900">Frais de mise en service</h4>
                        <p className="text-sm text-amber-800">
                          Des frais de mise en service de <span className="font-bold">{getOnboardingFeeAmount(company?.paymentStatus)}€ HT</span> seront facturés lors de votre première facture.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                <div className="space-y-4">
                  <h3 className="mb-3 text-lg font-semibold text-gray-900">
                    Ce qui est inclus :
                  </h3>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <CheckCircleIcon className="mt-0.5 h-6 w-6 flex-shrink-0 text-green-500" />
                      <div>
                        <div className="font-medium text-gray-900">
                          Accès à tous les modules
                        </div>
                        <div className="text-sm text-gray-600">
                          Communication, Prospection et Monitoring
                        </div>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircleIcon className="mt-0.5 h-6 w-6 flex-shrink-0 text-green-500" />
                      <div>
                        <div className="font-medium text-gray-900">
                          Support client prioritaire
                        </div>
                        <div className="text-sm text-gray-600">
                          Assistance technique et accompagnement
                        </div>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircleIcon className="mt-0.5 h-6 w-6 flex-shrink-0 text-green-500" />
                      <div>
                        <div className="font-medium text-gray-900">
                          Mises à jour régulières
                        </div>
                        <div className="text-sm text-gray-600">
                          Nouvelles fonctionnalités et améliorations continues
                        </div>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircleIcon className="mt-0.5 h-6 w-6 flex-shrink-0 text-green-500" />
                      <div>
                        <div className="font-medium text-gray-900">
                          Aucune limite d'utilisation
                        </div>
                        <div className="text-sm text-gray-600">
                          Utilisez tous les modules sans restriction
                        </div>
                      </div>
                    </li>
                  </ul>
                </div>
              </div>
            )}

            {/* CGV Acceptance */}
            <div className="rounded-lg bg-white p-6 shadow-md">
              <div className="mb-4 flex items-start gap-2">
                <DocumentTextIcon className="mt-0.5 h-6 w-6 flex-shrink-0 text-gray-600" />
                <h2 className="text-xl font-semibold text-gray-900">
                  Conditions Générales de Vente
                </h2>
              </div>
              
              <div className="mb-4 rounded-lg border border-blue-200 bg-blue-50 p-4">
                <p className="text-sm text-gray-700">
                  Avant de procéder au paiement, veuillez prendre connaissance et accepter nos Conditions Générales de Vente.
                </p>
              </div>

              <div className="mb-6 flex items-start gap-3">
                <input
                  type="checkbox"
                  id="accept-cgv"
                  checked={acceptedCGV}
                  onChange={(e) => setAcceptedCGV(e.target.checked)}
                  className="mt-1 h-5 w-5 cursor-pointer rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <label htmlFor="accept-cgv" className="flex-1 cursor-pointer text-sm text-gray-700">
                  J'ai lu et j'accepte les{' '}
                  <a
                    href={CGV_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-medium text-blue-600 underline hover:text-blue-700"
                  >
                    Conditions Générales de Vente
                  </a>
                  {' '}du module Croissance de Déclarations Juridiques
                </label>
              </div>

              {/* Payment Action Buttons */}
              <div className="space-y-3">
                <button
                  onClick={handleProceedToPayment}
                  disabled={!acceptedCGV || isCreatingSubscription}
                  className={`w-full rounded-lg px-6 py-4 font-semibold text-white shadow-lg transition-all ${
                    acceptedCGV && !isCreatingSubscription
                      ? 'bg-blue-600 hover:bg-blue-700 hover:shadow-xl'
                      : 'cursor-not-allowed bg-gray-400'
                  }`}
                >
                  {isCreatingSubscription ? (
                    <>
                      <ClockIcon className="mr-2 inline h-5 w-5 animate-spin" />
                      Préparation du paiement...
                    </>
                  ) : (
                    <>
                      <CreditCardIcon className="mr-2 inline h-5 w-5" />
                      Procéder au paiement
                    </>
                  )}
                </button>

                <button
                  onClick={handleGoBack}
                  disabled={isCreatingSubscription}
                  className="w-full rounded-lg bg-gray-100 px-6 py-3 font-semibold text-gray-700 transition-colors hover:bg-gray-200 disabled:opacity-50"
                >
                  Retour
                </button>
              </div>

              <div className="mt-4 flex items-start gap-2 text-sm text-gray-600">
                <ExclamationTriangleIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-blue-500" />
                <p>
                  Vous serez redirigé vers notre partenaire de paiement sécurisé ProAbono pour finaliser votre commande.
                </p>
              </div>
            </div>
          </div>

          {/* Right Column - Summary & Action */}
          <div className="lg:col-span-1">
            <div className="sticky top-6 rounded-lg bg-white p-6 shadow-md">
              <h2 className="mb-6 text-xl font-semibold text-gray-900">
                Récapitulatif
              </h2>

              {offerDetails && (
                <div className="space-y-4">
                  <div className="flex justify-between text-gray-600">
                    <span>Abonnement mensuel</span>
                    <span className="font-medium text-gray-900">
                      {formatPrice(offerDetails.AmountRecurrence)}
                    </span>
                  </div>
                  {/* Onboarding fee line */}
                  <div className="flex justify-between text-gray-600">
                    <span>Frais de mise en service</span>
                    {hasNoOnboardingFee(company?.paymentStatus) ? (
                      <span className="font-medium">
                        <span className="text-gray-400 line-through">{getOnboardingFeeAmount(company?.paymentStatus)}€</span>
                        <span className="ml-2 font-bold text-green-600">OFFERTS</span>
                      </span>
                    ) : has50PercentPromo(company?.paymentStatus) ? (
                      <span className="font-medium">
                        <span className="text-gray-400 line-through">{getOnboardingFeeAmount(company?.paymentStatus)}€</span>
                        <span className="ml-2 font-bold text-purple-600">{getDiscountedOnboardingFeeAmount(company?.paymentStatus)}€ HT</span>
                      </span>
                    ) : (
                      <span className="font-medium text-gray-900">
                        {getOnboardingFeeAmount(company?.paymentStatus)}€ HT
                      </span>
                    )}
                  </div>
                  <div className="border-t border-gray-200 pt-4">
                    <div className="flex justify-between text-lg font-bold text-gray-900">
                      <span>Total HT / mois</span>
                      <span>{formatPrice(offerDetails.AmountRecurrence)}</span>
                    </div>
                    {hasNoOnboardingFee(company?.paymentStatus) ? null : has50PercentPromo(company?.paymentStatus) ? (
                      <div className="mt-2 flex justify-between text-sm text-purple-700">
                        <span>+ Frais de mise en service (1ère facture)</span>
                        <span>{getDiscountedOnboardingFeeAmount(company?.paymentStatus)}€ HT <span className="text-xs">(-50%)</span></span>
                      </div>
                    ) : (
                      <div className="mt-2 flex justify-between text-sm text-amber-700">
                        <span>+ Frais de mise en service (1ère facture)</span>
                        <span>{getOnboardingFeeAmount(company?.paymentStatus)}€ HT</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Trust Indicators */}
        <div className="mt-12 rounded-lg bg-white p-8 shadow-md">
          <div className="grid grid-cols-1 gap-6 text-center md:grid-cols-3">
            <div className="flex flex-col items-center">
              <div className="mb-3 rounded-full bg-green-100 p-3">
                <CheckCircleIcon className="h-8 w-8 text-green-600" />
              </div>
              <h3 className="mb-1 font-semibold text-gray-900">Paiement sécurisé</h3>
              <p className="text-sm text-gray-600">
                Transactions cryptées et conformes PCI-DSS
              </p>
            </div>
            <div className="flex flex-col items-center">
              <div className="mb-3 rounded-full bg-blue-100 p-3">
                <ClockIcon className="h-8 w-8 text-blue-600" />
              </div>
              <h3 className="mb-1 font-semibold text-gray-900">Engagement 12 mois</h3>
              <p className="text-sm text-gray-600">
                Abonnement annuel reconduit automatiquement
              </p>
            </div>
            <div className="flex flex-col items-center">
              <div className="mb-3 rounded-full bg-purple-100 p-3">
                <DocumentTextIcon className="h-8 w-8 text-purple-600" />
              </div>
              <h3 className="mb-1 font-semibold text-gray-900">Facturation claire</h3>
              <p className="text-sm text-gray-600">
                Recevez vos factures par email chaque mois
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentCheckout;

